/**
 * FpStatsPage — FP donation statistics admin tool (§2.18 可捐赠FP统计).
 *
 * Shows:
 *   1. Summary cards (total FP, holders, donated, avg, max)
 *   2. Distribution histogram
 *   3. Top FP holders table
 */
import { Card, Col, Row, Statistic, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { FundOutlined, TeamOutlined, RiseOutlined } from '@ant-design/icons';
import { api } from '../../api/client';

export default function FpStatsPage() {
  const { data: summary } = useQuery({
    queryKey: ['fp-stats-summary'],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/fp-stats/summary');
      return res.data;
    },
    staleTime: 60_000,
  });

  const { data: distribution } = useQuery({
    queryKey: ['fp-stats-distribution'],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/fp-stats/distribution');
      return res.data;
    },
    staleTime: 60_000,
  });

  const { data: topHolders } = useQuery({
    queryKey: ['fp-stats-top-holders'],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/fp-stats/top-holders', { params: { limit: 20 } });
      return res.data;
    },
    staleTime: 60_000,
  });

  const fmt = (v: string | undefined) => {
    if (!v) return '0';
    const n = parseFloat(v);
    return isNaN(n) ? '0' : n.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  };

  const holderColumns = [
    { title: '#', width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户名', dataIndex: 'username', width: 140 },
    { title: '等级', dataIndex: 'level_code', width: 80 },
    { title: 'FP余额', dataIndex: 'fp', width: 150, render: (v: string) => fmt(v) },
    {
      title: '虚拟',
      dataIndex: 'is_bot',
      width: 70,
      render: (v: boolean) => v ? <Tag color="orange">是</Tag> : null,
    },
  ];

  const distColumns = [
    { title: '区间', dataIndex: 'label', width: 160 },
    { title: '用户数', dataIndex: 'count', width: 100 },
    {
      title: '占比',
      width: 120,
      render: (_: any, row: { count: number }) => {
        const total = summary?.total_users ?? 1;
        return `${((row.count / total) * 100).toFixed(1)}%`;
      },
    },
    {
      title: '',
      width: 300,
      render: (_: any, row: { count: number }) => {
        const total = summary?.total_users ?? 1;
        const pct = Math.min((row.count / total) * 100, 100);
        return (
          <div style={{ background: '#f0f0f0', borderRadius: 4, height: 16, width: '100%' }}>
            <div
              style={{
                background: '#1890ff',
                borderRadius: 4,
                height: 16,
                width: `${pct}%`,
                minWidth: pct > 0 ? 2 : 0,
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title="FP总量" value={fmt(summary?.total_fp)} prefix={<FundOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="持有用户" value={summary?.fp_holders ?? 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="平均FP" value={fmt(summary?.avg_fp)} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="最大FP" value={fmt(summary?.max_fp)} prefix={<RiseOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="已捐赠FP"
              value={fmt(summary?.total_fp_donated)}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="总用户数" value={summary?.total_users ?? 0} />
          </Card>
        </Col>
      </Row>

      {/* Distribution histogram */}
      <Card title="FP分布" size="small" style={{ marginBottom: 24 }}>
        <Table
          dataSource={distribution ?? []}
          columns={distColumns}
          rowKey="label"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Top holders */}
      <Card title="FP排行榜 (Top 20)" size="small">
        <Table
          dataSource={topHolders ?? []}
          columns={holderColumns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
}
